import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
  Grow,
  Fade
} from '@mui/material';
import { 
  AttachFile, 
  Close as CloseIcon,
  EditNote as EditNoteIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  CreateNewFolder
} from '@mui/icons-material';
import { Category } from '../types/forum.types';
import { useMarkdown } from '../hooks/useMarkdown';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Title,
  Code,
  Link as LinkIcon,
  Image,
  FormatQuote,
} from '@mui/icons-material';

interface NewPostDialogProps {
  open: boolean;
  categories: Category[];
  onClose: () => void;
  onSubmit: () => Promise<any>;
  title: string;
  content: string;
  tags: string;
  selectedCategory: number | null;
  selectedFile: File | null;
  onTitleChange: (value: string) => void;
  onContentChange: (value: string) => void;
  onTagsChange: (value: string) => void;
  onCategoryChange: (value: number | null) => void;
  onFileChange: (file: File | null) => void;
  isEditMode?: boolean;
}

export const NewPostDialog: React.FC<NewPostDialogProps> = ({
  open,
  categories,
  onClose,
  onSubmit,
  title,
  content,
  tags,
  selectedCategory,
  selectedFile,
  onTitleChange,
  onContentChange,
  onTagsChange,
  onCategoryChange,
  onFileChange,
  isEditMode = false
}) => {
  const { renderMarkdown } = useMarkdown();
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'split' | 'preview'>('split');

  const insertText = (before: string, after: string = '') => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const newText = content.substring(0, start) + 
                   before + 
                   (selectedText || 'text') + 
                   after + 
                   content.substring(end);
    
    onContentChange(newText);
    
    // Set cursor position after formatting
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + (selectedText || 'text').length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const formatActions = [
    { 
      icon: <Title />, 
      tooltip: 'Heading (Ctrl+H)', 
      action: () => insertText('# '),
      divider: false 
    },
    { 
      icon: <FormatBold />, 
      tooltip: 'Bold (Ctrl+B)', 
      action: () => insertText('**', '**'),
      divider: false 
    },
    { 
      icon: <FormatItalic />, 
      tooltip: 'Italic (Ctrl+I)', 
      action: () => insertText('*', '*'),
      divider: false 
    },
    { 
      icon: <Code />, 
      tooltip: 'Code (Ctrl+K)', 
      action: () => insertText('`', '`'),
      divider: true 
    },
    { 
      icon: <FormatListBulleted />, 
      tooltip: 'Bullet List', 
      action: () => insertText('- '),
      divider: false 
    },
    { 
      icon: <FormatListNumbered />, 
      tooltip: 'Numbered List', 
      action: () => insertText('1. '),
      divider: true 
    },
    { 
      icon: <LinkIcon />, 
      tooltip: 'Link (Ctrl+L)', 
      action: () => insertText('[', '](url)'),
      divider: false 
    },
    { 
      icon: <Image />, 
      tooltip: 'Image', 
      action: () => insertText('![alt text](', ')'),
      divider: false 
    },
    { 
      icon: <FormatQuote />, 
      tooltip: 'Quote', 
      action: () => insertText('> '),
      divider: false 
    },
  ];

  // Add keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            insertText('**', '**');
            break;
          case 'i':
            e.preventDefault();
            insertText('*', '*');
            break;
          case 'k':
            e.preventDefault();
            insertText('`', '`');
            break;
          case 'h':
            e.preventDefault();
            insertText('# ');
            break;
          case 'l':
            e.preventDefault();
            insertText('[', '](url)');
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [content]);

  const handleSubmit = async () => {
    if (!title || !content || !selectedCategory) return;
    
    setIsSubmitting(true);
    try {
      // Call the parent component's onSubmit handler
      // The parent component will handle closing the dialog
      await onSubmit();
    } catch (error) {
      console.error('Error submitting post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !isSubmitting && onClose()} 
      fullWidth 
      maxWidth="xl"
      PaperProps={{
        sx: { 
          borderRadius: isDarkTheme ? '8px' : '0px',
          bgcolor: isDarkTheme ? theme.palette.background.paper : 'background.paper',
          backgroundImage: 'none',
          height: '90vh',
          maxHeight: '900px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      TransitionComponent={Grow}
      transitionDuration={300}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: 2,
        borderBottom: '1px solid',
        borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditMode ? <EditNoteIcon color="primary" /> : <CreateNewFolder color="primary" />}
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {isEditMode ? 'Edit Post' : 'Create New Post'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ borderRadius: isDarkTheme ? '4px' : '0px' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent 
        sx={{ 
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 3,
          flex: 1,
          overflow: 'hidden'
        }}>
          {/* Header Section */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'flex-start', md: 'center' }
          }}>
            {/* Main Info */}
            <TextField
              autoFocus
              size="small"
              label="Title"
              placeholder="Write an interesting title..."
              fullWidth
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              sx={{ 
                flex: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkTheme ? alpha('#64AFE3', 0.3) : alpha(theme.palette.primary.main, 0.3)
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                  }
                }
              }}
            />
            
            <FormControl 
              size="small" 
              sx={{ 
                width: { xs: '100%', md: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkTheme ? alpha('#64AFE3', 0.3) : alpha(theme.palette.primary.main, 0.3)
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                  }
                }
              }}
            >
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                value={selectedCategory || ''}
                label="Category"
                onChange={(e) => onCategoryChange(e.target.value as number)}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              alignItems: 'center',
              width: { xs: '100%', md: 'auto' }
            }}>
              <input
                accept="image/*,.pdf,.doc,.docx"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={(e) => onFileChange(e.target.files?.[0] || null)}
              />
              <label htmlFor="file-upload">
                <Button
                  size="small"
                  component="span"
                  variant="outlined"
                  startIcon={<AttachFile />}
                  sx={{
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                    color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark,
                      bgcolor: isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  Attach
                </Button>
              </label>
              {selectedFile && (
                <Chip
                  label={selectedFile.name}
                  size="small"
                  onDelete={() => onFileChange(null)}
                  sx={{ 
                    maxWidth: { xs: '100%', md: 200 },
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    bgcolor: isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05),
                    color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                    '& .MuiChip-deleteIcon': {
                      color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                      '&:hover': {
                        color: isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark
                      }
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Tags Section */}
          <TextField
            size="small"
            label="Tags"
            value={tags}
            onChange={(e) => onTagsChange(e.target.value)}
            placeholder="Add tags separated by commas (e.g., programming, help, discussion)"
            sx={{ 
              width: { xs: '100%', md: '60%' },
              '& .MuiOutlinedInput-root': {
                borderRadius: isDarkTheme ? '4px' : '0px',
                bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : 'background.paper',
                '& fieldset': {
                  borderColor: isDarkTheme ? alpha('#64AFE3', 0.3) : alpha(theme.palette.primary.main, 0.3)
                },
                '&:hover fieldset': {
                  borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                }
              }
            }}
          />

          {/* View Mode Controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end'
          }}>
            <Box 
              sx={{ 
                display: 'inline-flex',
                bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.1),
                borderRadius: isDarkTheme ? '4px' : '0px',
                p: 0.5,
                border: '1px solid',
                borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.2) : theme.palette.divider
              }}
            >
              <Tooltip title="Edit View">
                <Button
                  size="small"
                  startIcon={<EditNoteIcon />}
                  onClick={() => setViewMode('edit')}
                  variant={viewMode === 'edit' ? 'contained' : 'text'}
                  sx={{ 
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5, 
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    textTransform: 'none',
                    bgcolor: viewMode === 'edit' ? (isDarkTheme ? '#64AFE3' : theme.palette.primary.main) : 'transparent',
                    color: viewMode === 'edit' ? (isDarkTheme ? '#000000' : 'white') : (isDarkTheme ? '#64AFE3' : theme.palette.primary.main),
                    '&:hover': {
                      bgcolor: viewMode === 'edit' ? (isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark) : (isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05))
                    }
                  }}
                >
                  Edit
                </Button>
              </Tooltip>
              
              <Tooltip title="Split View">
                <Button
                  size="small"
                  onClick={() => setViewMode('split')}
                  variant={viewMode === 'split' ? 'contained' : 'text'}
                  sx={{ 
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5, 
                    mx: 0.5,
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    textTransform: 'none',
                    bgcolor: viewMode === 'split' ? (isDarkTheme ? '#64AFE3' : theme.palette.primary.main) : 'transparent',
                    color: viewMode === 'split' ? (isDarkTheme ? '#000000' : 'white') : (isDarkTheme ? '#64AFE3' : theme.palette.primary.main),
                    '&:hover': {
                      bgcolor: viewMode === 'split' ? (isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark) : (isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05))
                    }
                  }}
                >
                  Split
                </Button>
              </Tooltip>
              
              <Tooltip title="Preview">
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setViewMode('preview')}
                  variant={viewMode === 'preview' ? 'contained' : 'text'}
                  sx={{ 
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5, 
                    borderRadius: isDarkTheme ? '4px' : '0px',
                    textTransform: 'none',
                    bgcolor: viewMode === 'preview' ? (isDarkTheme ? '#64AFE3' : theme.palette.primary.main) : 'transparent',
                    color: viewMode === 'preview' ? (isDarkTheme ? '#000000' : 'white') : (isDarkTheme ? '#64AFE3' : theme.palette.primary.main),
                    '&:hover': {
                      bgcolor: viewMode === 'preview' ? (isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark) : (isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05))
                    }
                  }}
                >
                  Preview
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Editor Section */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            height: { xs: '400px', md: '500px' },
            flex: 1,
            overflow: 'hidden'
          }}>
            {(viewMode === 'edit' || viewMode === 'split') && (
              <Fade in={true} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                flex: viewMode === 'split' ? 0.5 : 1,
                width: viewMode === 'split' ? '50%' : '100%'
          }}>
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                  overflow: 'hidden'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  pl: 1
                }}
              >
                Write
              </Typography>
              
              {/* Markdown Toolbar */}
                  <Paper
                    variant="outlined"
                    sx={{ 
                display: 'flex',
                      flexWrap: 'wrap',
                gap: 0.5,
                p: 1,
                      bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : 'background.secondary',
                      borderBottom: '1px solid',
                      borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : 'divider',
                      borderRadius: isDarkTheme ? '4px 4px 0 0' : '0px'
                    }}
                  >
                {formatActions.map((action, index) => (
                  <React.Fragment key={index}>
                        <Tooltip title={action.tooltip} arrow>
                      <IconButton 
                        size="small"
                        onClick={action.action}
                        sx={{ 
                              color: isDarkTheme ? alpha(theme.palette.text.primary, 0.7) : 'text.secondary',
                              borderRadius: isDarkTheme ? '4px' : '0px',
                          '&:hover': { 
                                color: isDarkTheme ? '#64AFE3' : 'primary.main',
                                bgcolor: isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        {action.icon}
                      </IconButton>
                    </Tooltip>
                        {action.divider && <Divider orientation="vertical" flexItem sx={{ borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.2) : 'divider' }} />}
                  </React.Fragment>
                ))}
                  </Paper>

              <TextField
                fullWidth
                multiline
                placeholder="Write your content here (Markdown supported)"
                value={content}
                onChange={(e) => onContentChange(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : 'background.secondary',
                    
                    '& fieldset': {
                      borderColor: isDarkTheme ? alpha('#64AFE3', 0.2) : alpha(theme.palette.primary.main, 0.2)
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkTheme ? alpha('#64AFE3', 0.4) : alpha(theme.palette.primary.main, 0.4)
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                    },
                    '& textarea': {
                      height: '100% !important',
                      color: isDarkTheme ? theme.palette.text.primary : 'text.primary',
                     
                      
                    }
                  }
                }}
              />
            </Box>
              </Fade>
            )}

            {(viewMode === 'preview' || viewMode === 'split') && (
              <Fade in={true} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                flex: viewMode === 'split' ? 0.5 : 1,
                width: viewMode === 'split' ? '50%' : '100%'
              }}>
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  pl: 1
                }}
              >
                Preview
              </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ 
                flex: 1,
                      border: '1px solid',
                      borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : 'divider',
                      borderRadius: isDarkTheme ? '4px' : '0px',
                p: 2,
                overflowY: 'auto',
                      bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : 'background.secondary',
                      '& img': {
                        maxWidth: '100%',
                        borderRadius: '4px'
                      },
                      '& pre': {
                        backgroundColor: isDarkTheme ? alpha('#000', 0.5) : alpha('#f5f5f5', 0.8),
                        padding: theme.spacing(1.5),
                        borderRadius: '4px',
                        overflowX: 'auto'
                      },
                      '& code': {
                        backgroundColor: isDarkTheme ? alpha('#000', 0.3) : alpha('#f5f5f5', 0.5),
                        padding: '0.2em 0.4em',
                        borderRadius: '3px',
                        fontSize: '0.85em',
                        fontFamily: 'monospace'
                      },
                      '& blockquote': {
                        borderLeft: `4px solid ${isDarkTheme ? '#64AFE3' : theme.palette.primary.main}`,
                        margin: '1em 0',
                        padding: '0.5em 1em',
                        backgroundColor: isDarkTheme ? alpha('#000', 0.2) : alpha('#f5f5f5', 0.3)
                      },
                      '& a': {
                        color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }
                    }}
                  >
                    {content ? (
                      renderMarkdown(content)
                    ) : (
                      <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'text.secondary'
                      }}>
                        <Typography variant="body2" color="inherit" align="center">
                          Your post preview will appear here
                        </Typography>
              </Box>
                    )}
                  </Paper>
            </Box>
              </Fade>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {tags ? (
              <>
                Tags: {tags.split(',').map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag.trim()}
                    size="small"
                    sx={{ 
                      mx: 0.5,
                      borderRadius: isDarkTheme ? '4px' : '0px',
                      bgcolor: isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.05),
                      color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                      height: 20,
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.7rem'
                      }
                    }}
                  />
                ))}
              </>
            ) : (
              'No tags added'
            )}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={onClose}
              variant="outlined"
              sx={{ 
                borderRadius: isDarkTheme ? '4px' : '0px',
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!title || !content || !selectedCategory || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ 
                borderRadius: isDarkTheme ? '4px' : '0px',
                bgcolor: isDarkTheme ? '#64AFE3' : 'primary.main',
                color: isDarkTheme ? '#000000' : 'primary.contrastText',
                px: 3,
                py: isSubmitting ? 1.2 : 1,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: isDarkTheme ? '#8ABFEB' : 'primary.dark'
                }
              }}
            >
              {isSubmitting 
                ? 'Processing...'
                : isEditMode
                  ? 'Update Post'
                  : 'Create Post'
              }
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}; 